import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

export const SUPPORT_EMAIL = 'support@excubia.io';
export const CONTACT_EMAIL = 'contact@excubia.io';
export const PARTNER_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Partnering with Excubia')}`;

export const KIND_LABELS = {
  support: 'Support',
  bug: 'Bug report',
  feature: 'Feature request',
  sales: 'Sales',
  partnership: 'Partnership',
};
const KIND_COLORS = { support: 'default', bug: 'error', feature: 'secondary', sales: 'info', partnership: 'warning' };

/** Categories a requester may choose (partnership has its own page). */
export const REQUEST_CATEGORIES = [
  { value: 'support', label: 'Support question' },
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'sales', label: 'Sales question' },
];

export const STATUS_LABELS = { new: 'New', open: 'Open', waiting: 'Waiting', resolved: 'Resolved', closed: 'Closed' };
const STATUS_COLORS = { new: 'info', open: 'primary', waiting: 'warning', resolved: 'success', closed: 'default' };
const PRIORITY_COLORS = { low: 'default', normal: 'default', high: 'warning', urgent: 'error' };

export const KindChip = ({ kind, size = 'small' }) => (
  <Chip label={KIND_LABELS[kind] || kind || 'n/a'} color={KIND_COLORS[kind] || 'default'} size={size} variant="outlined" />
);

/** `requesterView` shows "Awaiting your reply" when the last message came from Excubia. */
export const TicketStatusChip = ({ status, lastMessageFrom, requesterView = false, size = 'small' }) => {
  const awaiting = requesterView && lastMessageFrom === 'agent' && !['resolved', 'closed'].includes(status);
  if (awaiting) return <Chip label="Awaiting your reply" color="warning" size={size} />;
  return <Chip label={STATUS_LABELS[status] || status || 'n/a'} color={STATUS_COLORS[status] || 'default'} size={size} />;
};

export const PriorityChip = ({ priority, size = 'small' }) => (
  <Chip label={(priority || 'normal').toUpperCase()} color={PRIORITY_COLORS[priority] || 'default'} size={size} variant={priority === 'high' || priority === 'urgent' ? 'filled' : 'outlined'} />
);

export const relativeTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return formatDistanceToNow(d, { addSuffix: true });
};

/** Maps an express-validator 400 response to { fieldName: message }. */
export const fieldErrorsFrom = (err) => {
  const list = err?.response?.data?.errors;
  if (!Array.isArray(list)) return {};
  return list.reduce((acc, e) => {
    const key = e.path || e.param;
    if (key && !acc[key]) acc[key] = e.msg || 'Invalid value';
    return acc;
  }, {});
};

/** A friendly top-level message for a failed submission. */
export const submitErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 429) return 'You have sent several requests in a short time. Please wait a few minutes and try again, or email us directly.';
  if (status === 400) return err?.response?.data?.message === 'Validation failed' ? 'Please correct the highlighted fields.' : (err?.response?.data?.message || 'Please check the form and try again.');
  return err?.response?.data?.message || 'We could not send your request. Please try again or email us directly.';
};

/** Anti-spam field real users never see or fill; bots that do are silently ignored by the API. */
export const HoneypotField = ({ value, onChange }) => (
  <Box
    component="input"
    type="text"
    name="website"
    aria-hidden="true"
    tabIndex={-1}
    autoComplete="off"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden', opacity: 0 }}
  />
);

/** Prefill values for a signed-in user. */
export const requesterFromUser = (user) => ({
  name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
  email: user?.email || '',
  company: user?.company?.name || '',
});

/**
 * One message in a ticket thread. `mine` aligns the bubble right.
 */
export const MessageBubble = ({ mine, author, body, at, tone = 'default' }) => (
  <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 1.5 }}>
    <Box sx={{ maxWidth: { xs: '92%', sm: '78%' } }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: mine ? 'right' : 'left', mb: 0.25 }}>
        {author} · {at ? new Date(at).toLocaleString() : ''}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          px: 1.75, py: 1.25, borderRadius: 2,
          borderTopRightRadius: mine ? 4 : 16,
          borderTopLeftRadius: mine ? 16 : 4,
          bgcolor: tone === 'note' ? 'warning.light' : mine ? 'primary.main' : 'action.hover',
          color: tone === 'note' ? 'grey.900' : mine ? 'primary.contrastText' : 'text.primary',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{body}</Typography>
      </Paper>
    </Box>
  </Box>
);
